import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Car, Users, Fuel, Settings, Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const { t, language } = useLanguage();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [filters, setFilters] = useState({
    priceRange: "all",
    fuelType: "all",
    transmission: "all"
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  const fetchCars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      // Ensure UI order is always low → high
      const sorted = [...(response.data || [])].sort((a, b) => (Number(a.price_per_day) || 0) - (Number(b.price_per_day) || 0));
      setCars(sorted);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching cars:", error);
      toast.error(t('error'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCars();
  }, [fetchCars]);

  // Derived list: filter + always sort (low → high)
  const filteredCars = useMemo(() => {
    let filtered = [...cars];

    // Filter by price
    if (filters.priceRange !== "all") {
      if (filters.priceRange === "budget") {
        filtered = filtered.filter(car => car.price_per_day <= 100);
      } else if (filters.priceRange === "premium") {
        filtered = filtered.filter(car => car.price_per_day > 100 && car.price_per_day <= 500);
      } else if (filters.priceRange === "luxury") {
        filtered = filtered.filter(car => car.price_per_day > 500);
      }
    }

    // Filter by fuel type
    if (filters.fuelType !== "all") {
      filtered = filtered.filter(car => car.fuel_type.toLowerCase().includes(filters.fuelType.toLowerCase()));
    }

    // Filter by transmission
    if (filters.transmission !== "all") {
      filtered = filtered.filter(car => car.transmission.toLowerCase().includes(filters.transmission.toLowerCase()));
    }

    // Always sort by price: low → high
    filtered.sort((a, b) => (Number(a.price_per_day) || 0) - (Number(b.price_per_day) || 0));

    return filtered;
  }, [cars, filters]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t('message_sent'));
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error(t('message_error'));
    }
  };

  const handleCarClick = (carId) => {
    navigate(`/car/${carId}`);
  };

  const handleImageClick = (e, imageUrl, carName) => {
    e.stopPropagation();
    setSelectedImage({ url: imageUrl, name: carName });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Car className="h-8 w-8 text-yellow-600" style={{ filter: 'drop-shadow(0 0 8px rgba(197, 157, 95, 0.4))' }} />
              <span className="text-2xl font-bold text-white luxury-title tracking-wide">Rent Car Kaci</span>
            </div>
            <div className="hidden md:flex space-x-12">
              <a href="#home" className="text-gray-300 hover:text-yellow-600 transition-all font-medium luxury-text text-sm tracking-widest uppercase">{t('nav_home')}</a>
              <a href="#cars" className="text-gray-300 hover:text-yellow-600 transition-all font-medium luxury-text text-sm tracking-widest uppercase">{t('nav_collection')}</a>
              <a href="#faq" className="text-gray-300 hover:text-yellow-600 transition-all font-medium luxury-text text-sm tracking-widest uppercase">{t('nav_faq')}</a>
              <a href="#contact" className="text-gray-300 hover:text-yellow-600 transition-all font-medium luxury-text text-sm tracking-widest uppercase">{t('nav_contact')}</a>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <a 
                href="/admin/login"
                className="hidden md:block text-gray-400 hover:text-yellow-600 transition-colors luxury-text text-xs tracking-wider uppercase"
              >
                Admin
              </a>
              <Button 
                data-testid="contact-nav-button" 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} 
                className="bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-white border border-yellow-600/30 shadow-lg shadow-yellow-900/30 luxury-text tracking-wide"
              >
                {t('nav_contact')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-32 px-4 overflow-hidden min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_59e0653d-f3fc-4cd4-8791-890e601f995d/artifacts/kqyqy5yn_IMG_2680.jpeg"
            alt="Rent Car Kaci"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/65"></div>
        </div>
        <div className="hero-glow"></div>
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="text-center">
            <div className="mb-8 inline-block">
              <div className="luxury-text text-yellow-600 text-sm tracking-[0.3em] uppercase mb-4" style={{ textShadow: '0 0 20px rgba(197, 157, 95, 0.3)' }}>
                {t('hero_premium')}
              </div>
            </div>
            <h1 data-testid="hero-title" className="luxury-title text-5xl sm:text-6xl lg:text-8xl font-bold text-white mb-8 animate-fade-in leading-tight" style={{ textShadow: '0 4px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero_title_1')}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-600 to-yellow-400">{t('hero_title_2')}</span>
            </h1>
            <p data-testid="hero-subtitle" className="luxury-subtitle text-xl sm:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed" style={{ textShadow: '0 2px 20px rgba(0, 0, 0, 0.3)' }}>
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                data-testid="hero-cta-button"
                onClick={() => document.getElementById('cars').scrollIntoView({ behavior: 'smooth' })}
                size="lg" 
                className="bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-white px-12 py-7 text-lg font-semibold border border-yellow-600/30 shadow-2xl shadow-yellow-900/50 hover:shadow-yellow-900/70 transition-all luxury-text tracking-wider uppercase"
              >
                {t('hero_cta')}
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/355684401001?text=Përshëndetje!%20Dëshiroj%20informacion%20për%20qira%20makinash.', '_blank')}
                size="lg" 
                variant="outline"
                className="border-2 border-yellow-600/40 text-yellow-600 hover:bg-yellow-600/10 px-12 py-7 text-lg font-semibold backdrop-blur-sm transition-all luxury-text tracking-wider uppercase"
              >
                {t('hero_contact')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Car className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('feature_new_cars')}</h3>
                <p className="text-gray-600">{t('feature_new_cars_desc')}</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('feature_support')}</h3>
                <p className="text-gray-600">{t('feature_support_desc')}</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('feature_reasonable_prices')}</h3>
                <p className="text-gray-600">{t('feature_reasonable_prices_desc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cars Section */}
      <section id="cars" className="py-32 px-4" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #141414 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="luxury-text text-yellow-600 text-sm tracking-[0.3em] uppercase mb-6">
              {t('our_collection')}
            </div>
            <h2 data-testid="cars-section-title" className="luxury-title text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('collection_title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent mx-auto mb-8"></div>
            <p className="luxury-subtitle text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t('collection_subtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="mb-12 flex flex-wrap gap-4 justify-center">
            <select
              value={filters.priceRange}
              onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
              className="bg-zinc-900 border border-yellow-600/30 text-white px-6 py-3 rounded-lg luxury-text focus:outline-none focus:border-yellow-600"
            >
              <option value="all">{t('filter_price_all')}</option>
              <option value="budget">{t('filter_price_budget')}</option>
              <option value="premium">{t('filter_price_premium')}</option>
              <option value="luxury">{t('filter_price_luxury')}</option>
            </select>

            <select
              value={filters.fuelType}
              onChange={(e) => setFilters({...filters, fuelType: e.target.value})}
              className="bg-zinc-900 border border-yellow-600/30 text-white px-6 py-3 rounded-lg luxury-text focus:outline-none focus:border-yellow-600"
            >
              <option value="all">{t('filter_fuel_all')}</option>
              <option value="benzinë">{t('filter_fuel_petrol')}</option>
              <option value="naftë">{t('filter_fuel_diesel')}</option>
              <option value="elektrike">{t('filter_fuel_electric')}</option>
              <option value="hibride">{t('filter_fuel_hybrid')}</option>
            </select>

            <select
              value={filters.transmission}
              onChange={(e) => setFilters({...filters, transmission: e.target.value})}
              className="bg-zinc-900 border border-yellow-600/30 text-white px-6 py-3 rounded-lg luxury-text focus:outline-none focus:border-yellow-600"
            >
              <option value="all">{t('filter_trans_all')}</option>
              <option value="automatik">{t('filter_trans_auto')}</option>
              <option value="manual">{t('filter_trans_manual')}</option>
            </select>

            <button
              onClick={() => setFilters({ priceRange: "all", fuelType: "all", transmission: "all" })}
              className="bg-red-900/30 border border-red-600/30 text-red-400 px-6 py-3 rounded-lg luxury-text hover:bg-red-900/50 transition-colors"
            >
              {t('clear_filters')}
            </button>
          </div>

          {loading ? (
            <div data-testid="loading-indicator" className="text-center py-12">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-600">{t('loading_cars')}</p>
            </div>
          ) : filteredCars.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 luxury-subtitle text-xl">{t('no_cars_found')}</p>
              <Button 
                onClick={() => setFilters({ priceRange: "all", fuelType: "all", transmission: "all" })}
                className="mt-6 bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500"
              >
                {t('clear_filters')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {filteredCars.map((car) => (
                <Card 
                  key={car.id} 
                  data-testid={`car-card-${car.id}`}
                  className="car-card overflow-hidden transition-all"
                  onClick={() => handleCarClick(car.id)}
                >
                  <div className="overflow-hidden relative group bg-zinc-500 h-[280px] flex items-center justify-center p-4">
                    <img 
                      src={car.image_url} 
                      alt={`${car.brand} ${car.model}`}
                      className="w-full h-full object-contain cursor-pointer transition-transform duration-300"
                      onClick={(e) => handleImageClick(e, car.image_url, `${car.brand} ${car.model}`)}
                    />
                    <div 
                      className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                      onClick={(e) => handleImageClick(e, car.image_url, `${car.brand} ${car.model}`)}
                    >
                      <div className="text-white luxury-text text-sm tracking-wider uppercase">
                        🔍 {t('view_photo')}
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 z-10">
                      <span className={`badge ${car.available ? 'badge-available' : 'badge-unavailable'}`}>
                        {car.available ? t('available_status') : t('reserved_status')}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-8 bg-gradient-to-b from-zinc-900 to-black">
                    <div className="mb-6">
                      <h3 className="luxury-title text-2xl font-bold text-white mb-2">
                        {car.brand}
                      </h3>
                      <p className="luxury-subtitle text-lg text-gray-400">{car.model}</p>
                      <p className="luxury-text text-xs text-gray-500 mt-1 tracking-wider">{car.year}</p>
                    </div>
                    
                    <div className="space-y-3 mb-6 pb-6 border-b border-white/10">
                      <div className="flex items-center text-sm text-gray-400">
                        <Fuel className="h-4 w-4 mr-3 text-yellow-600" />
                        <span className="luxury-text">{car.fuel_type}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-400">
                        <Settings className="h-4 w-4 mr-3 text-yellow-600" />
                        <span className="luxury-text">{car.transmission}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-400">
                        <Users className="h-4 w-4 mr-3 text-yellow-600" />
                        <span className="luxury-text">{car.seats} {t('seats')}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="luxury-text text-xs text-gray-500 tracking-wider uppercase mb-1">{t('price_day')}</div>
                        <span className="luxury-title text-3xl font-bold text-yellow-600">
                          €{car.price_tiers && car.price_tiers.length > 0
                            ? Math.min(...car.price_tiers.map(t => t.price_per_day))
                            : car.price_per_day}
                        </span>
                        <span className="text-yellow-600/70 text-sm">/{t('per_day') || 'ditë'}</span>
                        {car.price_tiers && car.price_tiers.length > 0 && (
                          <div className="mt-2 space-y-0.5">
                            {car.price_tiers.map((tier, idx) => (
                              <p key={idx} className="text-gray-400 text-xs luxury-text">
                                {tier.min_days}-{tier.max_days ? tier.max_days : '+'} {t('days') || 'ditë'}: <span className="text-gray-200">€{tier.price_per_day}/{t('per_day') || 'ditë'}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button 
                        data-testid={`view-details-button-${car.id}`} 
                        className="bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-white border border-yellow-600/30 shadow-lg shadow-yellow-900/30 luxury-text tracking-wider uppercase text-xs px-6"
                      >
                        {t('view_details')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4" style={{ background: 'linear-gradient(180deg, #141414 0%, #0a0a0a 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="luxury-text text-yellow-600 text-sm tracking-[0.3em] uppercase mb-6">
              {t('faq_title')}
            </div>
            <h2 className="luxury-title text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('faq_subtitle')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent mx-auto"></div>
          </div>

          <div className="space-y-4">
            {[
              { question: t('faq_q1'), answer: t('faq_a1') },
              { question: t('faq_q2'), answer: t('faq_a2') },
              { question: t('faq_q3'), answer: t('faq_a3') },
              { question: t('faq_q4'), answer: t('faq_a4') },
              { question: t('faq_q5'), answer: t('faq_a5') },
              { question: t('faq_q6'), answer: t('faq_a6') }
            ].map((faq, index) => (
              <Card key={index} className="bg-zinc-900 border-yellow-600/20 hover:border-yellow-600/40 transition-all">
                <CardContent className="p-6">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer list-none">
                      <h3 className="luxury-title text-lg font-semibold text-white">{faq.question}</h3>
                      <span className="text-yellow-600 text-2xl group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="mt-4 text-gray-400 luxury-subtitle leading-relaxed">{faq.answer}</p>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="luxury-text text-yellow-600 text-sm tracking-[0.3em] uppercase mb-6">
              {t('contact_get_in_touch')}
            </div>
            <h2 data-testid="contact-section-title" className="luxury-title text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('contact_title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-600 to-transparent mx-auto mb-8"></div>
            <p className="luxury-subtitle text-xl text-gray-400">{t('contact_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="bg-zinc-900 border-yellow-600/20 hover:border-yellow-600/40 transition-all">
              <CardContent className="p-6">
                <a href="tel:+355684401001" className="flex items-start space-x-4 group">
                  <div className="w-12 h-12 bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-900/50 transition-colors">
                    <Phone className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="luxury-title font-bold text-lg mb-1 text-white">{t('contact_phone')}</h3>
                    <p className="luxury-text text-gray-400 text-sm">+355 68 440 1001</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-yellow-600/20 hover:border-yellow-600/40 transition-all">
              <CardContent className="p-6">
                <a href="mailto:xhuljokaci72@gmail.com" className="flex items-start space-x-4 group">
                  <div className="w-12 h-12 bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-900/50 transition-colors">
                    <Mail className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="luxury-title font-bold text-lg mb-1 text-white">{t('contact_email')}</h3>
                    <p className="luxury-text text-gray-400 text-sm break-all">xhuljokaci72@gmail.com</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <a 
                  href="https://wa.me/355684401001?text=Përshëndetje!%20Dëshiroj%20informacion%20për%20qira%20makinash." 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 group"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                    <svg className="h-6 w-6 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">WhatsApp</h3>
                    <p className="text-gray-600 text-sm">+355 68 440 1001</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <a 
                  href="https://instagram.com/rentcar_kaci" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 group"
                >
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-pink-200 transition-colors">
                    <svg className="h-6 w-6 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Instagram</h3>
                    <p className="text-gray-600 text-sm">@rentcar_kaci</p>
                  </div>
                </a>
              </CardContent>
            </Card>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="contact-name">{t('contact_name')}</Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">{t('contact_email')}</Label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-phone">{t('contact_phone')}</Label>
                  <Input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">{t('contact_your_message')}</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-textarea"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    rows={5}
                    className="mt-2"
                  />
                </div>
                <Button 
                  data-testid="contact-submit-button"
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg font-semibold"
                >
                  {t('send_message')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Car className="h-8 w-8" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Rent Car Kaci</span>
          </div>
          <p className="text-gray-400 mb-4">{t('footer_description')}</p>
          <div className="flex justify-center items-center space-x-6 mb-4">
            <a 
              href="/admin/login"
              className="text-gray-500 hover:text-yellow-600 transition-colors luxury-text text-xs tracking-wider"
            >
              Admin
            </a>
            <span className="text-gray-700">|</span>
            <a 
              href="https://instagram.com/rentcar_kaci" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-pink-400 transition-colors"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://wa.me/355684401001?text=Përshëndetje!%20Dëshiroj%20informacion%20për%20qira%20makinash." 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-green-400 transition-colors"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
          </div>
          <p className="text-sm text-gray-500">© 2025 Rent Car Kaci. {t('footer_rights')}.</p>
        </div>
      </footer>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-screen">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-600 transition-colors"
            >
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <img 
              src={selectedImage.url}
              alt={selectedImage.name}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
              <h3 className="luxury-title text-2xl font-bold text-white text-center">
                {selectedImage.name}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/355684401001?text=Përshëndetje!%20Dëshiroj%20informacion%20për%20qira%20makinash."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110"
        data-testid="floating-whatsapp-button"
      >
        <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default HomePage;
